"use client";

import { cn } from "@/lib/helpers/utils";
import { Fragment, useEffect, useState } from "react";
import { Ellipsis, ExternalLink, Eye, EyeOff } from "lucide-react";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "./table";
import { surahs } from "@/lib/data/surahs";
import { SameWordsType } from "@/actions/get-same-words";
import { WbwRootType } from "@/actions/get-root-words";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuShortcut,
} from "./dropdown-menu";
import {
  DropdownMenuGroup,
  DropdownMenuTrigger,
} from "@radix-ui/react-dropdown-menu";
import { Button } from "./button";
import { amiri, uthmaniHafs } from "@/app/fonts";
import { useQuranOptions } from "@/app/providers/QuranContextProvider";
import { useIndexedDB, UseIndexedDBResult } from "@/lib/hooks/useIndexedDb";
import { QuranResponseType } from "@/lib/types";
import useLocalStorage from "@/lib/hooks/useLocalStorage";
import { Spinner } from "./spinner";
import QuranAyah from "./quran-ayah";
import {
  QURAN_DATABASE_KEY_NAME,
  QURAN_DATABASE_NAME,
  QURAN_DATABASE_TABLES,
  QURAN_DATABASE_VERSION,
} from "@/lib/constants";
import {
  fetchViewAyahPage,
  findPageNumberFromLocalStorage,
} from "@/lib/helpers/functions";

export default function QuranRootOrSameWordsTable({
  tableHeadData,
  tableBodyData,
}: {
  tableHeadData: { [key: string]: string };
  tableBodyData: Array<SameWordsType | WbwRootType>;
}) {
  const [page, setPage] = useState<number>(
    10 > tableBodyData.length ? tableBodyData.length : 10,
  );
  const [openedId, setOpenedId] = useState<number>(-1);
  const [expandedRows, setExpandedRows] = useState<number[]>([]);

  const { getRecord } = useIndexedDB<QuranResponseType[]>(
    QURAN_DATABASE_NAME,
    QURAN_DATABASE_TABLES,
    QURAN_DATABASE_KEY_NAME,
    QURAN_DATABASE_VERSION,
  );
  const { saveRecords } = useIndexedDB<QuranResponseType>(
    QURAN_DATABASE_NAME,
    QURAN_DATABASE_TABLES,
    QURAN_DATABASE_KEY_NAME,
    QURAN_DATABASE_VERSION,
  );

  const toggleRow = (index: number) => {
    if (expandedRows.includes(index)) {
      setExpandedRows(expandedRows.filter((i) => i !== index));
    } else {
      setExpandedRows([...expandedRows, index]);
    }
  };

  const itemsToShow: typeof tableBodyData = [];
  for (var i = 0; i < page; i++) {
    itemsToShow.push(tableBodyData[i]);
  }

  return (
    <div className="mt-5 overflow-hidden">
      <Table className="p-4">
        <TableHeader>
          <TableRow style={{ direction: "rtl" }}>
            <TableHead>Actions</TableHead>
            {Object.entries(tableHeadData)
              .reverse()
              .map((el) => (
                <TableHead key={el[1]}>{el[1]}</TableHead>
              ))}
          </TableRow>
        </TableHeader>
        <TableBody>
          {itemsToShow.map((val, i) => (
            <Fragment key={"fragment-" + i}>
              <TableRow
                className={`[&>*]:transition-all [&>*]:duration-300 ${
                  expandedRows.includes(i)
                    ? "bg-muted/20 [&>*]:py-6"
                    : "[&>*]:py-4"
                }`}
              >
                <TableCell>
                  <DropdownSingleMenuForAll
                    rowId={val.id}
                    rowNum={i}
                    toggleRow={toggleRow}
                    opened={openedId}
                    setOpened={setOpenedId}
                  />
                </TableCell>
                <TableCell>{val.verse}</TableCell>
                <TableCell className={amiri.className + " text-xl"}>
                  {surahs[val.surah - 1].nameArabic}
                </TableCell>
                <TableCell
                  className={uthmaniHafs.className + " text-xl md:text-3xl"}
                  style={{ direction: "rtl" }}
                >
                  {/* {val.text_uthmani.replace("ا۟", "")} */}
                  {val.text.replace("۟", "ْ")}
                </TableCell>
              </TableRow>
              {expandedRows.includes(i) && (
                <TableRow className="hover:bg-transparent [&>*]:transition-all [&>*]:duration-500">
                  <TableCell colSpan={4} className="p-2">
                    <Table>
                      <TableBody>
                        <TableRow className="hover:bg-transparent [&>*]:transition-all [&>*]:duration-500">
                          <TableCell className="p-0">
                            <ShowAyah
                              item={val}
                              getRecord={getRecord}
                              saveRecords={saveRecords}
                            />
                          </TableCell>
                        </TableRow>
                      </TableBody>
                    </Table>
                  </TableCell>
                </TableRow>
              )}
            </Fragment>
          ))}
        </TableBody>
      </Table>
      {tableBodyData.length > page && (
        <div className="w-full text-center">
          <Button className="mt-5" onClick={() => setPage((p) => p + 10)}>
            Load More
          </Button>
        </div>
      )}
    </div>
  );
}

const ShowAyah = ({
  item,
  getRecord,
  saveRecords,
}: {
  item: SameWordsType | WbwRootType;
  getRecord: UseIndexedDBResult<QuranResponseType[]>["getRecord"];
  saveRecords: UseIndexedDBResult<QuranResponseType>["saveRecords"];
}) => {
  const [ayah, setAyah] = useState<QuranResponseType | null>(null);
  const { quranMushaf, data, activeTranslations } = useQuranOptions();
  const [localStorage, setLocalSotrage] = useLocalStorage<
    { start: number; end: number }[] | undefined
  >(quranMushaf.type, undefined);

  useEffect(() => {
    let mushafPage: QuranResponseType[];
    let pageNumber = 0;

    pageNumber = findPageNumberFromLocalStorage(item.verse_id, localStorage);

    const fetchPage = async () => {
      let pagesRsult = await fetchViewAyahPage({
        quranMushaf,
        activeTranslations,
        setAyah,
        data,
        item: { surah: item.surah, verse: item.verse },
        pageNumber,
        getRecord,
        saveRecords,
        localStorage,
      });

      if (pagesRsult) setLocalSotrage(pagesRsult);
    };
    fetchPage();
    //eslint-disable-next-line
  }, [quranMushaf.type, quranMushaf.lines]);

  return (
    <>
      {!ayah && (
        <div className="h-30 text-center flex items-center justify-center">
          <Spinner className="text-primary" size={"large"}>
            <span>Loading......</span>
          </Spinner>
        </div>
      )}

      {ayah && <QuranAyah data={ayah} shouldTrack={false} />}
    </>
  );
};

const DropdownSingleMenuForAll = ({
  rowId,
  rowNum,
  opened,
  setOpened,
  toggleRow,
}: {
  rowId: number;
  rowNum: number;
  opened: number;
  setOpened: React.Dispatch<React.SetStateAction<number>>;
  toggleRow: (row: number) => void;
}) => {
  const [hide, setHide] = useState(true);
  return (
    <DropdownMenu
      open={rowId === opened}
      onOpenChange={(open) => setOpened(open ? rowId : -1)}
    >
      <DropdownMenuTrigger asChild>
        <div className="flex justify-start">
          <Button
            size="icon"
            variant="ghost"
            className="shadow-none"
            aria-label="Edit item"
            onClick={() => setOpened(rowId)}
          >
            <Ellipsis size={16} strokeWidth={2} aria-hidden="true" />
          </Button>
        </div>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-56">
        <DropdownMenuGroup>
          <DropdownMenuItem
            onClick={() => {
              toggleRow(rowNum);
              setHide((p) => !p);
            }}
          >
            {hide ? (
              <Eye className="size-9 text-primary" />
            ) : (
              <EyeOff className="size-9 text-primary" />
            )}
            <span>{hide ? "View" : "Hide"} Ayah</span>
            <DropdownMenuShortcut>⌘V</DropdownMenuShortcut>
          </DropdownMenuItem>
          <DropdownMenuItem onClick={() => console.log("")}>
            <ExternalLink className="size-9 text-primary" />
            <span>Open Ayah</span>
            <DropdownMenuShortcut>⌘O</DropdownMenuShortcut>
          </DropdownMenuItem>
        </DropdownMenuGroup>
      </DropdownMenuContent>
    </DropdownMenu>
  );
};
